/**
 * ModuleSelector - Checkbox grid for module selection
 *
 * Displays all available modules grouped by category with checkboxes.
 * Now uses DB-driven data from useAppFeatures hook.
 */

import React from 'react';
import { useAppFeatures } from '../../hooks/useAppFeatures.js';

export function ModuleSelector({ selectedModules, onChange }) {
  const { features, featuresByGroup, groups, isLoading } = useAppFeatures();

  const handleToggle = (moduleId) => {
    const newModules = selectedModules.includes(moduleId)
      ? selectedModules.filter(m => m !== moduleId)
      : [...selectedModules, moduleId];
    onChange(newModules);
  };

  const handleSelectAll = () => {
    onChange(features.map(m => m.id));
  };

  const handleSelectNone = () => {
    // Keep at least dashboard
    onChange(['dashboard']);
  };

  const handleSelectGroup = (groupKey, select) => {
    const groupModules = (featuresByGroup[groupKey] || []).map(m => m.id);
    if (select) {
      const newModules = [...new Set([...selectedModules, ...groupModules])];
      onChange(newModules);
    } else {
      // Don't remove dashboard
      const newModules = selectedModules.filter(m => !groupModules.includes(m) || m === 'dashboard');
      onChange(newModules);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-slate-400 py-4">Chargement des modules...</div>;
  }

  const sortedGroups = Object.entries(groups).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-4">
      {/* Header with quick actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-300">
          Modules ({selectedModules.length}/{features.length})
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
          >
            Tout
          </button>
          <button
            type="button"
            onClick={handleSelectNone}
            className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
          >
            Aucun
          </button>
        </div>
      </div>

      {/* Groups */}
      <div className="space-y-3">
        {sortedGroups.map(([groupKey, group]) => {
          const groupModules = featuresByGroup[groupKey] || [];
          if (groupModules.length === 0) return null;

          const selectedInGroup = groupModules.filter(m => selectedModules.includes(m.id)).length;
          const allSelected = selectedInGroup === groupModules.length;
          const someSelected = selectedInGroup > 0 && !allSelected;

          return (
            <div key={groupKey} className="bg-slate-700/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => el && (el.indeterminate = someSelected)}
                    onChange={(e) => handleSelectGroup(groupKey, e.target.checked)}
                    className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <span className="text-sm font-medium text-slate-300">{group.label}</span>
                </label>
                <span className="text-xs text-slate-500">
                  {selectedInGroup}/{groupModules.length}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 pl-6">
                {groupModules.map(module => (
                  <label
                    key={module.id}
                    className="flex items-center gap-2 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={selectedModules.includes(module.id)}
                      onChange={() => handleToggle(module.id)}
                      disabled={module.id === 'dashboard'} // Dashboard is always required
                      className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 disabled:opacity-50"
                    />
                    <span className={`text-sm ${
                      selectedModules.includes(module.id)
                        ? 'text-white'
                        : 'text-slate-400 group-hover:text-slate-300'
                    }`}>
                      {module.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ModuleSelector;
